import { Component, Prop, State, Element, Build, h } from '@stencil/core';
import { ACTION_REGISTRY } from '../../../../../actions/_generated-registry';
import type { ActionContext } from '../../../../../actions/types';
import { sendAbTowerMetric } from '../../shared/metrics';
import { runWrapperActions } from '../../../../../actions/runtime';

const COMPONENT_NAME = 'dh-form-preview-type-1';

// Per-form "already voted" cookie prefix — must match the rating action default.
const RATING_COOKIE_PREFIX = 'form-rating-';

// The CTA runs this action to resolve the editor/uploader URL per-visitor (see ui/dh-open-editor).
const CTA_ACTION = 'ui:dh-open-editor';
const ACTIONS_ATTR = 'trigger-actions';
const DATA_ATTR = 'action-data';

// [MEP-1397] The CTA href already carries the per-visitor guestId (added at render by Herald), so the
// editor-link round-trip is currently redundant and turned OFF — the CTA just navigates its own href,
// with no form-original-id check. The action + wiring are kept intact (not deleted); set this to
// `true` to re-enable per-click editor-link minting.
const EDITOR_LINK_ENABLED: boolean = false;

/**
 * Form-landing hero ("intro") — a dark, full-height stage that shows a dimmed
 * backend-generated form preview image behind a centered heading and primary CTA,
 * with breadcrumbs at the top and an optional footer bar (rating / description).
 * Fills the viewport below the site header (height measured at runtime). Breadcrumbs
 * and copy render light over the dark, overlaid preview.
 *
 * @category DocHub
 * @slot breadcrumbs - {required}, <dh-breadcrumbs-type-1> — rendered inverse (light) on the dark stage
 * @slot heading - {required}, <dh-heading variant="1" tag="h1"> — centered, white, clamped to 3 lines
 * @slot button - {required}, <dh-button tag="a" variant="primary" href="…"> — the "Get Form" CTA.
 *   With `form-original-id` set, the click resolves the editor URL through the backend (so the
 *   uploader link carries the per-visitor `guestId`) and the `href` is the fallback; without it the
 *   CTA is a plain link. `form-id` is unrelated — it drives only the interactive star rating.
 * @example
 *   <dh-form-preview-type-1
 *     image="/dochub/form-preview/w9-preview.png"
 *     image-alt="W9 form preview"
 *     form-id="308563"
 *     description="The W-9 form is an IRS request for a taxpayer identification number…"
 *     rating-value="3.6"
 *     rating-text="3.6 out of 5"
 *     votes-text="159 votes">
 *     <dh-breadcrumbs-type-1 slot="breadcrumbs" items='[{"label":"Home","href":"/"},{"label":"W-9 form"}]'></dh-breadcrumbs-type-1>
 *     <dh-heading slot="heading" variant="1" tag="h1" align="center">Form W-9 2024-2026</dh-heading>
 *     <dh-button slot="button" tag="a" variant="primary" href="https://dochub.com/fillable-form/308563">Get Form</dh-button>
 *   </dh-form-preview-type-1>
 */
@Component({
  tag: 'dh-form-preview-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DhFormPreviewType1 {
  @Element() el!: HTMLElement;

  /**
   * Form preview image URL used as the `<dh-picture>` fallback `src`. Supplied by
   * the backend (it generates per-form previews) — not bundled with the component.
   * @mediaType image
   * @mediaGroup preview
   * @mediaRole url
   */
  @Prop() image!: string;

  /**
   * Optional density/viewport `srcset` for the preview (e.g. "img.png 1x, img@2x.png 2x").
   * @mediaType image
   * @mediaGroup preview
   * @mediaRole srcset
   */
  @Prop() imageSrcSet?: string;

  /**
   * Optional WebP `srcset` rendered as a `<source type="image/webp">`.
   * @mediaType image
   * @mediaGroup preview
   * @mediaRole webp-srcset
   */
  @Prop() imageWebpSrcSet?: string;

  /**
   * Intrinsic width of the preview image, set as the `<img width>` attribute so the
   * browser reserves the correct aspect-ratio box before the image loads (prevents
   * the layout jump/resize on load). Pair with `imageHeight`. Defaults to the W-9
   * preview ratio (336×436); pass the real backend dimensions for other forms.
   * @default 336
   */
  @Prop() imageWidth: number = 336;

  /**
   * Intrinsic height of the preview image (`<img height>` attribute). See `imageWidth`.
   * @default 436
   */
  @Prop() imageHeight: number = 436;

  /**
   * Form id used only for the interactive star rating: it identifies the form when submitting a
   * vote (`rating:dochub-vote`) and keys the per-form "already voted" cookie. It does NOT drive the
   * "Get Form" CTA — that is keyed off `formOriginalId`. Required for `rating-interactive`.
   */
  @Prop() formId?: string;

  /**
   * Original form id (the uploader `f` value). When set, clicking the "Get Form" CTA resolves the
   * editor/uploader URL through the backend (`editorLinkEndpoint`) instead of navigating to the
   * button's static `href`, so Herald can inject the per-visitor `guestId`. Without it the CTA stays
   * a plain link.
   */
  @Prop({ attribute: 'form-original-id' }) formOriginalId?: string;

  /**
   * Optional per-page endpoint override for the CTA editor-link call. Empty ⇒ the endpoint of the
   * active API mode (see the `EDITOR_LINK_API` switch in `actions/ui/dh-open-editor`). Ignored when
   * `formOriginalId` is empty.
   * @default ""
   */
  @Prop() editorLinkEndpoint: string = '';

  /**
   * Accessible alt text for the preview image, e.g. "W9 form preview".
   * @content
   * @mediaGroup preview
   * @mediaRole alt
   */
  @Prop() imageAlt: string = '';

  /**
   * Collapsible form description shown in the footer. When set, a details toggle
   * appears on the left of the footer bar. Leave empty to hide the toggle.
   * @content
   */
  @Prop() description: string = '';

  /**
   * Footer toggle label when the description is collapsed. @default "See details"
   * @content
   */
  @Prop() seeDetailsLabel: string = 'See details';

  /**
   * Footer toggle label when the description is expanded. @default "Hide details"
   * @content
   */
  @Prop() hideDetailsLabel: string = 'Hide details';

  /**
   * Average rating from 0 to 5, used to fill the footer stars. When > 0 the rating
   * block (stars + texts) renders on the right of the footer bar.
   * @default 0
   */
  @Prop() ratingValue: number = 0;

  /**
   * Rating text shown next to the stars, e.g. "3.6 out of 5".
   * @content
   */
  @Prop() ratingText: string = '';

  /**
   * Votes/reviews count text shown after the rating, e.g. "159 votes".
   * @content
   */
  @Prop() votesText: string = '';

  /**
   * Let visitors rate the form (hover preview + click) instead of showing a read-only
   * average. Requires `form-id`. On click the vote is submitted via the `rating:dochub-vote`
   * action (POST + cookie) and the row locks; a returning voter (cookie present) stays
   * locked. @default false
   */
  @Prop() ratingInteractive: boolean = false;

  /**
   * Optional SKS click-tracking hook for the "Get Form" CTA. When set, its value is
   * rendered as a `data-sks-track` attribute on the CTA wrapper, so the external
   * `sksTrackerHandler` script (delegated `closest('[data-sks-track]')`) fires
   * `window.sksTracker[value]()` on click. Leave empty to omit tracking on this page.
   * Editable per page in the admin.
   *
   * Allowed values: `trackBeforeAction`, `trackImpression`, `trackRegistration`,
   * `trackPayment`, `trackAfterAction`, `trackTally`.
   */
  @Prop() sksTrack?: string;

  /**
   * AB Tower click metric fired on the "Get Form" CTA. On click the value is POSTed as
   * `{ abTower: <value> }` to `/api/send-metrics` (same-origin, no CSRF required, `keepalive`
   * so it survives the CTA's navigation). Restores the metric the legacy PHP page sent via
   * `data-metric-ab-tower` + the global `clickTracker` script, which Herald pages don't ship.
   * Set to an empty string to disable tracking on this page. @default "dh_lp_get_form_button_click"
   */
  @Prop() abTowerMetric: string = 'dh_lp_get_form_button_click';

  @State() detailsOpen: boolean = false;
  @State() hasVoted: boolean = false;
  @State() votedScore: number | null = null;
  @State() votesDisplay: string = '';

  componentWillLoad() {
    // Mirror the votes text into state so a successful vote can bump the count.
    this.votesDisplay = this.votesText;
  }

  // The "Get Form" CTA is a slotted <a> inside dh-section; slot relocation drops JSX onClick
  // handlers, so the AB Tower metric is wired as a native listener on the rendered wrapper here
  // (the click bubbles from the button up to it). Kept as a field so it can be removed on teardown.
  private ctaEl?: HTMLElement;
  private handleCtaClick = (event: MouseEvent) => {
    sendAbTowerMetric(this.abTowerMetric);

    // With a form id the CTA target is resolved through the ui:dh-open-editor action at click time
    // (so the uploader link carries the per-visitor guestId); without one it stays a plain link.
    // The action binding is stamped on the wrapper as attributes; run it here because dh-section
    // slot relocation drops JSX onClick, so the metric + action share this native listener.
    if (!EDITOR_LINK_ENABLED || !this.formOriginalId || !this.ctaEl) return;
    event.preventDefault();
    void runWrapperActions(this.ctaEl, event, this);
  };

  // Payload for the CTA action, keyed by action name (runElementActions reads the per-action slice).
  private ctaActionData(): string {
    return JSON.stringify({
      [CTA_ACTION]: { formOriginalId: this.formOriginalId, editorLinkEndpoint: this.editorLinkEndpoint },
    });
  }

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Attach when either side effect is live: the AB Tower metric OR the editor-link action.
    if (this.abTowerMetric || this.formOriginalId) {
      this.ctaEl = (this.el.querySelector(`.${COMPONENT_NAME}__cta`) as HTMLElement | null) ?? undefined;
      this.ctaEl?.addEventListener('click', this.handleCtaClick);
    }

    // A returning visitor who already voted: lock the rating and show their vote.
    if (this.ratingInteractive && this.formId) {
      const cookie = document.cookie.split('; ').find(row => row.startsWith(`${RATING_COOKIE_PREFIX}${this.formId}=`));
      if (cookie) {
        const value = parseFloat(cookie.split('=')[1]);
        this.hasVoted = true;
        if (!Number.isNaN(value)) this.votedScore = value;
      }
    }
  }

  disconnectedCallback() {
    this.ctaEl?.removeEventListener('click', this.handleCtaClick);
  }

  // The interactive rating fired a vote. Lock the row optimistically (the vote is cast,
  // so no more hover/fill — just the user's score) and bump the count immediately, then
  // persist via the rating action (POST + cookie) in the background.
  private handleRatingSelect = async (event: CustomEvent<number>) => {
    if (this.hasVoted || !this.formId) return;
    const stars = event.detail;

    this.hasVoted = true;
    this.votedScore = stars;
    this.votesDisplay = this.bumpVotes(this.votesDisplay);

    const loader = ACTION_REGISTRY['rating:dochub-vote'];
    if (!loader) return;

    try {
      const mod = (await loader()) as { default: new () => { execute(c: ActionContext): Promise<void> } };
      const action = new mod.default();
      const context: ActionContext = {
        event: event as unknown as MouseEvent,
        element: this.el,
        component: this,
        data: { formId: this.formId, stars },
      };
      await action.execute(context);
    } catch {
      /* the local vote stays committed even if persistence fails */
    }
  };

  // Increment the first number found in the votes label (e.g. "159 votes" → "160 votes").
  private bumpVotes(text: string): string {
    return text.replace(/\d[\d,]*/, match => {
      const n = parseInt(match.replace(/,/g, ''), 10);
      return Number.isNaN(n) ? match : (n + 1).toLocaleString('en-US');
    });
  }

  private toggleDetails = () => {
    this.detailsOpen = !this.detailsOpen;
  };

  private renderRating() {
    if (!(this.ratingValue > 0) && !this.ratingText) return null;

    // Interactive only when explicitly enabled, a form id is present, and not yet voted.
    const interactive = this.ratingInteractive && !!this.formId && !this.hasVoted;
    const score = this.hasVoted && this.votedScore !== null ? this.votedScore : this.ratingValue;

    return (
      <div class={`${COMPONENT_NAME}__rating`}>
        <dh-rating-stars
          class={`${COMPONENT_NAME}__stars`}
          score={score}
          step={0.5}
          readonly={!interactive}
          label={this.ratingText || `Rated ${score} out of 5`}
          onRatingSelect={this.handleRatingSelect}
        />
        {this.ratingText && <span class={`${COMPONENT_NAME}__rating-value`}>{this.ratingText}</span>}
        {this.votesDisplay && <span class={`${COMPONENT_NAME}__rating-votes`}>{this.votesDisplay}</span>}
      </div>
    );
  }

  render() {
    const hasDetails = !!this.description;
    const rating = this.renderRating();
    const hasFooter = hasDetails || !!rating;
    return (
      <dh-section class={COMPONENT_NAME} noContainer yGap="none" yGapLg="none" role="region">
        <div class={`${COMPONENT_NAME}__inner dh-section--container`}>
          <div class={`${COMPONENT_NAME}__breadcrumbs`}>
            <slot name="breadcrumbs" />
          </div>

          <div class={`${COMPONENT_NAME}__preview`}>
            <dh-picture
              class={`${COMPONENT_NAME}__image`}
              src={this.image}
              srcSet={this.imageSrcSet}
              webpSrcSet={this.imageWebpSrcSet}
              alt={this.imageAlt}
              width={this.imageWidth}
              height={this.imageHeight}
              aspectRatio=""
              lazy="false"
            />
          </div>

          <div class={`${COMPONENT_NAME}__content`}>
            <div class={`${COMPONENT_NAME}__heading`}>
              <slot name="heading" />
            </div>
            <div
              class={`${COMPONENT_NAME}__cta`}
              data-sks-track={this.sksTrack || undefined}
              {...(EDITOR_LINK_ENABLED && this.formOriginalId ? { [ACTIONS_ATTR]: CTA_ACTION, [DATA_ATTR]: this.ctaActionData() } : {})}
            >
              <slot name="button" />
            </div>
          </div>

          {hasFooter && (
            <div class={`${COMPONENT_NAME}__footer`}>
              <div class={`${COMPONENT_NAME}__footer-bar`}>
                {hasDetails && (
                  <button
                    type="button"
                    class={{
                      [`${COMPONENT_NAME}__details-trigger`]: true,
                      [`${COMPONENT_NAME}__details-trigger--open`]: this.detailsOpen,
                    }}
                    aria-expanded={this.detailsOpen ? 'true' : 'false'}
                    aria-controls={`${COMPONENT_NAME}__details-panel`}
                    onClick={this.toggleDetails}
                  >
                    {this.detailsOpen ? this.hideDetailsLabel : this.seeDetailsLabel}
                  </button>
                )}
                {rating}
              </div>

              {hasDetails && (
                <div
                  id={`${COMPONENT_NAME}__details-panel`}
                  class={{
                    [`${COMPONENT_NAME}__details-panel`]: true,
                    [`${COMPONENT_NAME}__details-panel--open`]: this.detailsOpen,
                  }}
                  aria-hidden={this.detailsOpen ? 'false' : 'true'}
                >
                  <p class={`${COMPONENT_NAME}__details-text`}>{this.description}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </dh-section>
    );
  }
}
